import PropTypes from 'prop-types';
import { cloneElement } from 'react';
import { cva } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/shared/utils/cn.js';

// --- Variants Definitions ---

const buttonVariants = cva(
  'flex items-center justify-center rounded-xl font-medium transition-all duration-200',
  {
    variants: {
      variant: {
        primary:
          'bg-primary-100 hover:bg-primary-80 disabled:bg-primary-30 transform text-white shadow-lg hover:scale-105 hover:shadow-xl',

        secondary:
          'bg-tertiary-10 text-tertiary-80 hover:bg-tertiary-20 disabled:bg-tertiary-6 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 dark:disabled:bg-gray-800',

        outline:
          'border-primary-100 text-primary-100 hover:bg-primary-10 disabled:border-primary-30 disabled:text-primary-30 border-2 dark:border-orange-500 dark:text-orange-400 dark:hover:bg-orange-900/30 dark:disabled:border-orange-700/50',

        danger:
          'bg-red-600 text-white shadow-md hover:bg-red-700 disabled:bg-red-400',

        success:
          'bg-green-600 text-white shadow-md hover:bg-green-700 disabled:bg-green-400',

        ghost:
          'text-tertiary-80 hover:bg-tertiary-10 disabled:text-tertiary-40 bg-transparent hover:shadow-none dark:text-gray-300 dark:hover:bg-gray-800/50 dark:disabled:text-gray-600',

        gradient:
          'transform bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg hover:scale-105 hover:from-blue-600 hover:to-purple-700 hover:shadow-xl disabled:from-blue-300 disabled:to-purple-400',
      },
      size: {
        sm: 'px-3 py-1.5 text-sm',
        md: 'px-4 py-2 text-base',
        lg: 'px-6 py-3 text-lg',
        xl: 'px-8 py-4 text-xl',
      },
      fullWidth: {
        true: 'w-full',
      },
      disabled: {
        true: 'cursor-not-allowed opacity-70',
        false: 'cursor-pointer',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      fullWidth: false,
      disabled: false,
    },
  },
);

const spinnerVariants = cva('flex animate-spin items-center justify-center', {
  variants: {
    size: {
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-6 w-6',
      xl: 'h-7 w-7',
    },
    iconPosition: {
      left: 'mr-2',
      right: 'ml-2',
      center: '',
    },
  },
  defaultVariants: {
    size: 'md',
    iconPosition: 'left',
  },
});

// --- Component ---

const Button = ({
  children,
  type = 'button',
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  isLoading = false,
  loading = false,
  disabled = false,
  onClick,
  className,
  icon,
  iconPosition = 'left',
  openInNewTab = false,
  ...props
}) => {
  const loadingState = isLoading || loading;

  const handleClick = (e) => {
    if (openInNewTab && onClick) {
      e.preventDefault();
      window.open(window.location.href, '_blank');
    }
    if (onClick) {
      onClick(e);
    }
  };

  const renderIcon = (iconElement, position) => {
    if (!iconElement) return null;

    const iconClasses = cn(
      'flex shrink-0 items-center justify-center',
      position === 'left' && 'mr-2',
      position === 'right' && 'ml-2',
    );

    return cloneElement(iconElement, {
      className: cn(iconElement.props.className, iconClasses),
    });
  };

  return (
    <button
      type={type}
      disabled={disabled || loadingState}
      onClick={handleClick}
      className={cn(
        buttonVariants({
          variant,
          size,
          fullWidth,
          disabled: disabled || loadingState,
        }),
        className,
      )}
      {...props}
    >
      {loadingState ? (
        <Loader2
          className={spinnerVariants({
            size,
            iconPosition: 'center',
          })}
        />
      ) : (
        <>
          {iconPosition === 'center' && icon ? (
            renderIcon(icon, 'center')
          ) : (
            <>
              {icon && iconPosition === 'right' && renderIcon(icon, 'right')}
              {children}
              {icon && iconPosition === 'left' && renderIcon(icon, 'left')}
            </>
          )}
        </>
      )}
    </button>
  );
};

Button.propTypes = {
  children: PropTypes.node,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  variant: PropTypes.oneOf([
    'primary',
    'secondary',
    'outline',
    'danger',
    'success',
    'ghost',
    'gradient',
  ]),
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  fullWidth: PropTypes.bool,
  isLoading: PropTypes.bool,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
  icon: PropTypes.node,
  iconPosition: PropTypes.oneOf(['left', 'right', 'center']),
  openInNewTab: PropTypes.bool,
};

export default Button;
